import { Pipe, PipeTransform } from '@angular/core';
import { utcToCentralDisplay } from '../utils/timezone-utils';

/**
 * Status of a subscription's last run, or of one run in its history (ARAP-1910).
 * The backend sends it as lastRunStatus (subscriptions list) or runStatus (run history).
 */
export type RunStatus = 'RUNNING' | 'COMPLETED' | 'NO_DATA' | 'FAILED' | 'DID_NOT_FINISH' | 'NOT_RUN';

export type RunStatusTone = 'running' | 'success' | 'warning' | 'danger' | 'neutral';

export interface RunStatusView {
  status: RunStatus;
  label: string;
  tone: RunStatusTone;
  /** Font Awesome solid glyph, shown next to the label (never on its own) */
  icon: string;
  /** Second line under the label: when it ran, or why it failed */
  detail: string;
  /** Longer explanation for the tooltip */
  hint: string;
  /** True when the run left a file that can be downloaded */
  hasFile: boolean;
}

/** The fields the status is read from, on a subscription or on one run */
export interface RunStatusSource {
  lastRunStatus?: string;
  lastRunStartDttm?: string;
  lastRunEndDttm?: string;
  lastRunMessage?: string;
  runStatus?: string;
  jobStatus?: string;
  startDttm?: string;
  endDttm?: string;
  failureReason?: string;
  displayMessage?: string;
  storageURL?: string;
}

const KNOWN: RunStatus[] = ['RUNNING', 'COMPLETED', 'NO_DATA', 'FAILED', 'DID_NOT_FINISH', 'NOT_RUN'];

const VIEWS: Record<RunStatus, { label: string; tone: RunStatusTone; icon: string; hint: string }> = {
  RUNNING: { label: 'Running', tone: 'running', icon: '\uf1ce',
    hint: 'The report is being created. This page updates on its own.' },
  COMPLETED: { label: 'Completed', tone: 'success', icon: '\uf058',
    hint: 'The report was created and is ready to download.' },
  NO_DATA: { label: 'No data', tone: 'warning', icon: '\uf06a',
    hint: 'The report ran, but no rows matched its filters, so the file is empty.' },
  FAILED: { label: 'Failed', tone: 'danger', icon: '\uf057',
    hint: 'The report could not be created.' },
  DID_NOT_FINISH: { label: "Didn't finish", tone: 'danger', icon: '\uf253',
    hint: 'The run started over an hour ago and never finished.' },
  NOT_RUN: { label: 'Not run yet', tone: 'neutral', icon: '\uf017',
    hint: 'This subscription has not run yet.' },
};

const MAX_DETAIL = 120;

/** Status of a subscription's last run, or of one run, with a fallback for records from before ARAP-1910 */
export function runStatusOf(source: RunStatusSource | null | undefined): RunStatus {
  if (!source) {
    return 'NOT_RUN';
  }
  const sent = String(source.lastRunStatus || source.runStatus || '').toUpperCase() as RunStatus;
  if (KNOWN.includes(sent)) {
    return sent;
  }
  const job = String(source.jobStatus || '').toUpperCase();
  if (job === 'IN_PROGRESS') {
    return 'RUNNING';
  }
  if (job === 'FAILED') {
    return 'FAILED';
  }
  return source.endDttm || source.lastRunEndDttm ? 'COMPLETED' : 'NOT_RUN';
}

function when(timestamp: string | undefined): string {
  return timestamp ? utcToCentralDisplay(timestamp, 'full') : '';
}

export function runStatusView(source: RunStatusSource | null | undefined): RunStatusView {
  const status = runStatusOf(source);
  const view = VIEWS[status];
  const start = source?.lastRunStartDttm || source?.startDttm;
  const end = source?.lastRunEndDttm || source?.endDttm;
  const reason = String(source?.lastRunMessage || source?.failureReason || source?.displayMessage || '').trim();
  let detail = '';
  let hint = view.hint;
  switch (status) {
    case 'RUNNING':
    case 'DID_NOT_FINISH':
      detail = start ? `Started ${when(start)}` : '';
      break;
    case 'COMPLETED':
      detail = when(end);
      break;
    case 'NO_DATA':
      detail = end ? `Empty file, ${when(end)}` : 'Empty file';
      break;
    case 'FAILED':
      detail = reason.length > MAX_DETAIL ? `${reason.slice(0, MAX_DETAIL - 3)}...` : reason || when(end);
      hint = reason ? `${view.hint} ${reason}` : view.hint;
      break;
  }
  return { status, label: view.label, tone: view.tone, icon: view.icon, detail, hint,
    hasFile: status === 'COMPLETED' || status === 'NO_DATA' };
}

/** {{ subscription | runStatus }} gives the label, colour, icon and second line for a run status */
@Pipe({ name: 'runStatus', standalone: true })
export class RunStatusPipe implements PipeTransform {
  transform(source: RunStatusSource | null | undefined): RunStatusView {
    return runStatusView(source);
  }
}
